import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useUnmount } from "react-use";
import { LiveChatUser } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { WebRTCConnection } from "@scrile/api-provider/dist/api/WebRTCProvider";
import { StreamingHelper } from "@scrile/streaming-client/build";
import { useTranslation } from "locales";
import useAuthUser from "hooks/useAuthUser";
import useCallController from "hooks/useCallController";
import useConferenceRecording, { RecordingResult, MAX_RECORDING_DURATION_MS } from "hooks/useConferenceRecording";
import useNetworkQuality from "hooks/useNetworkQuality";
import { waitForMeetingArtifacts } from "lib/aiApi";
import { copyDumpToClipboard } from "lib/diagnosticDump";
import { SYSTEM_MESSAGE_PREFIX } from "lib/emitter";
import providers from "lib/providers";
import { markRecordingAnnounced } from "lib/recordingSegmentUploader";
import { buildTranscriptDownloadUrl, waitForTranscription } from "lib/recordingTranscriptWatcher";
import { startRingback, stopRingback } from "lib/ringback";
import BaseAvatar from "components/BaseAvatar";
import BaseIcon from "components/BaseIcon";
import NetworkQualityBadge from "components/NetworkQualityBadge";
import { SendDataEvent } from "../../const";
import { Routes } from "../../router/config";
import { ProduceStream } from "../../types";
import "./style.scss";

interface Props {
  callId: string;
}

// Module-scope monotonic counter so each PageDirectCall instance gets a
// stable numeric tag (#1, #2, ...) visible in the diagnostic dump. If the
// outer page is being remounted independently of useCallController (e.g. a
// parent rebuilds the route subtree), the PageDirectCall instance id will
// move in lockstep with — or independently of — the useCallController id.
let __pageDirectCallInstanceSeq = 0;

interface LocationState {
  callConnection?: WebRTCConnection;
  isCaller?: boolean;
  voiceOnly?: boolean;
  opponentUser?: any;
}

/**
 * Build a synthetic LiveChatUser-shaped object from a UserPublicData so
 * the recording compositor (which expects ProduceStream → LiveChatUser →
 * UserPublicData.avatar) can render avatars for voice-only or video-off
 * tiles. Fields irrelevant to recording (broadcastingTime, joinTime,
 * etc.) get cheap defaults.
 */
function asLiveChatUser(u: UserPublicData | null | undefined, id: string, screenName: string): LiveChatUser {
  return {
    id,
    chatId: id,
    userId: id,
    screenName,
    user: u || null,
    broadcasting: true,
    broadcastingTime: "",
    joinTime: "",
    lastOnline: "",
    muted: false,
  };
}

async function sendSystemMessage(threadId: string, text: string) {
  try {
    await providers.MessagesProvider.sendExtendedMessages({
      data: { text: `${SYSTEM_MESSAGE_PREFIX}${text}`, threadIds: [threadId] },
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[direct-call] Failed to send system message:", e);
  }
}

/**
 * Look up (or create on the fly) the personal 1-on-1 thread between the
 * auth user and the opponent. `findPersonal` is the canonical
 * find-or-create endpoint — safe to call even before any message exists
 * between the pair. Returns null only on transport / auth errors;
 * recording still works, just without the system message and the
 * file-message attached to the thread.
 */
async function findDmThreadId(opponentId: string): Promise<string | null> {
  try {
    const thread = await providers.MessageThreadsProvider.findPersonal({ opponentId: String(opponentId) });
    return thread?.id || null;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[direct-call] DM thread lookup failed:", e);
    return null;
  }
}

export default function PageDirectCall({ callId }: Props) {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { user } = useAuthUser();

  // ---- Lifecycle diagnostics (no behavioral effect) ------------------------
  // One MOUNT log per instance: assigned during the first render via useRef
  // so the id is stable across re-renders but unique across remounts. One
  // UNMOUNT log via useUnmount (fires only on the real unmount commit).
  // Paired with the useCallController instance ids, these make a spurious
  // React remount obvious: PDC #N unmount + PDC #N+1 mount within ms of
  // each other, same callId, same location.pathname.
  const pdcInstanceIdRef = useRef<number>(0);
  if (pdcInstanceIdRef.current === 0) {
    __pageDirectCallInstanceSeq += 1;
    pdcInstanceIdRef.current = __pageDirectCallInstanceSeq;
    // eslint-disable-next-line no-console
    console.warn(
      `[PAGE-LIFECYCLE] PageDirectCall MOUNT instance#${pdcInstanceIdRef.current} callId=${callId} pathname=${
        location.pathname
      } search=${location.search} locKey=${location.key ?? "null"} userId=${user?.id ?? "null"} historyAction=${
        history.action
      } historyLen=${history.length} t=${Math.round(performance.now())}ms env=${process.env.NODE_ENV}`
    );
  }
  useUnmount(() => {
    // eslint-disable-next-line no-console
    console.warn(
      `[PAGE-LIFECYCLE] PageDirectCall UNMOUNT instance#${pdcInstanceIdRef.current} callId=${callId} pathname=${
        location.pathname
      } search=${location.search} locKey=${location.key ?? "null"} historyAction=${history.action} historyLen=${
        history.length
      } visibility=${typeof document !== "undefined" ? document.visibilityState : "?"} hasFocus=${
        typeof document !== "undefined" ? document.hasFocus() : "?"
      } online=${typeof navigator !== "undefined" ? navigator.onLine : "?"} t=${Math.round(performance.now())}ms`
    );
  });

  // Route-element identity change detector. React Router v5 mints a new
  // location.key on every push/replace (including a same-pathname replace
  // such as onModalClose's history.replace(backgroundLocation)). A new key
  // inside one PageDirectCall instance lifetime means the parent saw a
  // navigation tick but DIDN'T remount us — useful as a control: if we
  // observe a remount WITHOUT a key change here, the cause is a re-render
  // higher up the tree creating a fresh route element type identity (the
  // suspected MainRouterView IIFE).
  const lastLocKeyRef = useRef<string | undefined>(location.key);
  if (lastLocKeyRef.current !== location.key) {
    // eslint-disable-next-line no-console
    console.warn(
      `[PAGE-LIFECYCLE] location.key changed instance#${pdcInstanceIdRef.current} callId=${callId} oldKey=${
        lastLocKeyRef.current ?? "null"
      } newKey=${location.key ?? "null"} pathname=${location.pathname} historyAction=${history.action} t=${Math.round(
        performance.now()
      )}ms`
    );
    lastLocKeyRef.current = location.key;
  }

  // Auth user identity change detector. AuthUserContext re-allocates a new
  // [state, dispatch] tuple on every dispatch (no equality check), and any
  // SET_LOGIN_DATA emit (token refresh, syncCookie, login flows) walks that
  // path. If a user-id flip happens during an active call, we want to know.
  const lastUserIdRef = useRef<string | undefined>(user?.id ? String(user.id) : undefined);
  const currentUserId = user?.id ? String(user.id) : undefined;
  if (lastUserIdRef.current !== currentUserId) {
    // eslint-disable-next-line no-console
    console.warn(
      `[PAGE-LIFECYCLE] auth user identity change instance#${pdcInstanceIdRef.current} callId=${callId} oldId=${
        lastUserIdRef.current ?? "null"
      } newId=${currentUserId ?? "null"} t=${Math.round(performance.now())}ms`
    );
    lastUserIdRef.current = currentUserId;
  }
  // -------------------------------------------------------------------------

  // Every intentional navigation to /call/:id carries router state (caller:
  // MessagesList, callee: IncomingCallModal). A state-less mount is therefore
  // always a reload / restore / deep-link — default to `false` so the initial
  // phase is "connecting" (rejoin via findActive), not a bogus "Calling..."
  // with ringback. The real role is derived from the call DTO below.
  const isCaller = location.state?.isCaller ?? false;
  const voiceOnly = location.state?.voiceOnly ?? new URLSearchParams(location.search).has("voiceOnly");
  const opponentFromState = location.state?.opponentUser;

  const {
    call,
    callPhase,
    endReason,
    localStream,
    remoteStream,
    mediaConstraints,
    toggleVideo,
    toggleAudio,
    endCall,
    producerWebRTC,
    consumerWebRTC,
    streamsStarted,
    connectionStatus,
  } = useCallController({
    callId,
    initialConnection: location.state?.callConnection,
    isCaller,
    voiceOnly,
  });

  // Network quality
  // useNetworkQuality needs a stable StreamingHelper reference.
  // Polling stays enabled as long as we have a producer helper — even
  // during reconnecting / disconnected / failed phases. The hook's
  // internal getStats() catches transport errors and skips the tick,
  // so polling adapts naturally to transport state. If we gated this
  // on `callPhase === "connected"` (as before), the `quality` value
  // would freeze at its last sampled reading during reconnect, and
  // [NET-QUALITY] logs in those windows would lie about real-time
  // quality (they'd just echo the pre-reconnect snapshot).
  const fallbackWebRTC = useRef(new StreamingHelper());
  const { quality } = useNetworkQuality(producerWebRTC || fallbackWebRTC.current, !!producerWebRTC);

  // Track quality transitions for post-mortem diagnostics. Sustained low
  // quality (≤2 for >60s) is the canonical signal that SFU/network is
  // degraded — needed to attribute 2-3 min call drops to infrastructure.
  // Promoted to warn so it lands in diagnosticDump (info is dropped).
  //
  // Gate intentionally permits reconnecting/disconnected phases — the
  // quality reading right before/during a reconnect is the MOST useful
  // diagnostic state. Only ringing (no transports yet) and ended
  // (cleanup) are excluded.
  const lastQualityRef = useRef<number | null>(null);
  useEffect(() => {
    if (callPhase === "ringing" || callPhase === "ended") return;
    if (lastQualityRef.current === quality) return;
    // eslint-disable-next-line no-console
    console.warn(
      `[NET-QUALITY] direct quality=${lastQualityRef.current}→${quality} callPhase=${callPhase} callId=${callId}`
    );
    lastQualityRef.current = quality;
  }, [quality, callPhase, callId]);

  // Opponent user info
  const opponent: UserPublicData | null = call
    ? String(call.callerId) === String(user?.id)
      ? call.receiver
      : call.caller
    : opponentFromState || null;
  const opponentName = opponent?.screenName || (opponent as any)?.username || "User";

  // Real caller role from the call DTO — survives reload (the router-state
  // `isCaller` is a default in that case). Used for cosmetic caller-only
  // behaviour like the ringback tone.
  const effectiveIsCaller = call && user ? String(call.callerId) === String(user.id) : isCaller;

  // Recording
  const { recording, recordingStartedAt, uploading, uploadError, startRecording, stopRecording, updateStreams } =
    useConferenceRecording();
  const [remoteRecordingStartedAt, setRemoteRecordingStartedAt] = useState<number | null>(null);
  const dmThreadIdRef = useRef<string | null>(null);
  const dmThreadLookupRef = useRef<Promise<string | null> | null>(null);

  // Lazily resolve the DM thread on first need so we don't fire an extra
  // GraphQL query for every call that never gets recorded.
  const resolveDmThreadId = useCallback(async (): Promise<string | null> => {
    if (dmThreadIdRef.current) return dmThreadIdRef.current;
    if (!opponent?.id) return null;
    if (!dmThreadLookupRef.current) {
      dmThreadLookupRef.current = findDmThreadId(String(opponent.id)).then((id) => {
        dmThreadIdRef.current = id;
        return id;
      });
    }
    return dmThreadLookupRef.current;
  }, [opponent?.id]);

  // Build the ProduceStream[] the recorder expects from useCallController's
  // local/remote MediaStreams. Memoised on the underlying stream objects +
  // mute toggles — re-creating the array reference is what tells
  // `updateStreams` to refresh the canvas compositor + audio mix.
  const recordingStreams: ProduceStream[] = useMemo(() => {
    const result: ProduceStream[] = [];
    if (user) {
      const meUser = asLiveChatUser(user as unknown as UserPublicData, String(user.id), user.screenName || "Me");
      result.push({
        id: String(user.id),
        user: meUser,
        stream: localStream,
        audioStream: localStream,
        hasAudio: !!mediaConstraints.audioEnabled,
        hasVideo: !!mediaConstraints.videoEnabled && localStream.getVideoTracks().some((t) => t.readyState === "live"),
        isScreen: false,
      });
    }
    if (opponent) {
      const oppId = String(opponent.id);
      const oppUser = asLiveChatUser(opponent, oppId, opponentName);
      result.push({
        id: oppId,
        user: oppUser,
        stream: remoteStream,
        hasAudio: remoteStream.getAudioTracks().some((t) => t.readyState === "live"),
        hasVideo: remoteStream.getVideoTracks().some((t) => t.readyState === "live"),
        isScreen: false,
      });
    }
    return result;
  }, [
    user,
    opponent,
    opponentName,
    localStream,
    remoteStream,
    mediaConstraints.audioEnabled,
    mediaConstraints.videoEnabled,
  ]);

  // Keep the recording compositor in sync if camera/mic toggles or the
  // remote producer changes mid-recording.
  const recordingRef = useRef(recording);
  recordingRef.current = recording;
  useEffect(() => {
    if (recordingRef.current) updateStreams(recordingStreams);
  }, [recordingStreams, updateStreams]);

  // Subscribe to the consumer data channel so we know when the OTHER side
  // started/stopped recording. PRODUCER_FINISHED_STREAM is handled inline in
  // useCallController; recording events are extra listeners on the same
  // EventEmitter — they coexist cleanly.
  useEffect(() => {
    const client = consumerWebRTC?.webRTCData?.consumer?.client;
    if (!client) return;
    const onData = (msg: { event?: SendDataEvent }) => {
      if (!msg || !msg.event) return;
      if (msg.event === SendDataEvent.RECORDING_STARTED) {
        setRemoteRecordingStartedAt(Date.now());
      } else if (msg.event === SendDataEvent.RECORDING_STOPPED) {
        setRemoteRecordingStartedAt(null);
      }
    };
    client.on("data", onData);
    return () => {
      try {
        client.off("data", onData);
      } catch {
        /* may already be closed */
      }
    };
    // `streamsStarted` bumps whenever the consumer is (re)created — re-bind
    // the listener even though the `consumerWebRTC` ref identity might not
    // have changed.
  }, [consumerWebRTC, streamsStarted]);

  // Backup signal path: subscribe to the DM thread for system messages.
  //
  // Why a backup is needed: in 1-on-1 calls the data channel is the primary
  // "the peer started recording" notification (above), but it can miss the
  // event entirely in real production:
  //   - producer.signalingProducer.readyState was not yet "open" when the
  //     caller clicked Record (ProducerClient.sendRawData silently no-ops)
  //   - the listener got attached AFTER the data-channel message was emitted
  //     (race during a reconnect / transport rebuild)
  //   - the SFU dropped the app-data message under load
  // Group conferences (ConferenceView) already use the thread-message path
  // as their sole signal — and it Just Works because GraphQL subscriptions
  // buffer events, no DataChannel timing race. Mirror that pattern here.
  //
  // Caller already posts `__SYSTEM__:Recording started` to the DM thread on
  // record start (PageDirectCall:423) and `__SYSTEM__:Recording stopped` /
  // `Recording available: …` on stop. We just need to listen.
  useEffect(() => {
    let cancelled = false;
    let sub: { unsubscribe: () => void } | null = null;

    const applyFromText = (text: string | null | undefined, timeIso: string | null | undefined) => {
      if (!text || !text.startsWith(SYSTEM_MESSAGE_PREFIX)) return;
      const body = text.replace(SYSTEM_MESSAGE_PREFIX, "").trim();
      if (body.startsWith("Recording started")) {
        const t = timeIso ? Date.parse(timeIso) : Date.now();
        setRemoteRecordingStartedAt(Number.isFinite(t) ? t : Date.now());
      } else if (
        body.startsWith("Recording available:") ||
        body.startsWith("Recording stopped") ||
        body.startsWith("Video call ended")
      ) {
        setRemoteRecordingStartedAt(null);
      }
    };

    (async () => {
      const threadId = await resolveDmThreadId();
      if (cancelled || !threadId) return;

      // Reconcile on join: a recent "Recording started" with no
      // following "stopped"/"available" means recording is still in
      // progress when we joined the call. Walk newest → oldest, first
      // recording-relevant entry wins.
      try {
        const resp = await providers.MessagesProvider.find({
          data: { threadId, sort: "TIME,DESC", page: 0 },
        });
        if (cancelled) return;
        for (const m of resp.result) {
          const txt = m.text || "";
          if (!txt.startsWith(SYSTEM_MESSAGE_PREFIX)) continue;
          const body = txt.replace(SYSTEM_MESSAGE_PREFIX, "").trim();
          if (
            body.startsWith("Recording available:") ||
            body.startsWith("Recording stopped") ||
            body.startsWith("Video call ended")
          ) {
            break;
          }
          if (body.startsWith("Recording started")) {
            const t = Date.parse(m.time);
            setRemoteRecordingStartedAt(Number.isFinite(t) ? t : Date.now());
            break;
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[direct-call] failed to fetch initial recording state:", e);
      }

      try {
        const s = await providers.MessagesProvider.subscribeToThread(threadId, (msg: Message) => {
          if (cancelled) return;
          applyFromText(msg.text, msg.time);
        });
        if (cancelled) {
          s.unsubscribe();
          return;
        }
        sub = s;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[direct-call] subscribeToThread failed:", e);
      }
    })();

    return () => {
      cancelled = true;
      sub?.unsubscribe();
      // Don't reset state on cleanup: a re-render that re-runs this
      // effect (opponent.id change after late call data load) would
      // erase a perfectly valid in-progress recording state. The
      // initial-scan above will re-establish it.
    };
  }, [resolveDmThreadId]);

  // Best-effort: notify the other side we started/stopped recording. If
  // the producer client isn't ready (rare race during connection setup),
  // skip — the recording still works locally, the remote just won't see
  // the indicator.
  const sendRecordingEvent = useCallback(
    async (event: SendDataEvent) => {
      try {
        await producerWebRTC?.webRTCData?.producer?.client?.sendData({ event });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[direct-call] Failed to broadcast recording event:", e);
      }
    },
    [producerWebRTC]
  );

  // After stopRecording resolves, mirror what ConferenceView does for group
  // calls: if the upload produced a file, post "Recording available: <url>"
  // and watch for the async transcript / coach pipeline to finish so the chat
  // gets the follow-up links. If the upload failed (or recording was too
  // short), fall back to "Recording stopped" so the REC indicator on the
  // remote side still clears.
  // NOTE: an earlier version of this file relied on the backend
  // (recordingUpload.js) to attach the recording as a message because we pass
  // threadId in metadata. It doesn't — the backend only writes
  // recordings_meta.json and returns JSON. The client is responsible for the
  // chat-side message; this helper is that responsibility.
  const postRecordingResult = useCallback(async (result: RecordingResult | null, threadId: string | null) => {
    if (!threadId) return;
    if (!result) {
      void sendSystemMessage(threadId, "Recording stopped");
      return;
    }
    await sendSystemMessage(threadId, `Recording available: ${result.url}`);
    if (!result.id) return;
    const recordingId = result.id;
    // Confirm to the server so its auto-announce safety net won't re-post.
    void markRecordingAnnounced(recordingId);
    void (async () => {
      try {
        const watch = await waitForTranscription(recordingId);
        if (watch.status === "failed") {
          const err = watch.recording?.transcription?.error || "unknown error";
          await sendSystemMessage(threadId, `Transcript failed: ${err}`);
          return;
        }
        if (watch.status !== "done") return;
        const url = buildTranscriptDownloadUrl(recordingId, "txt");
        const segs = watch.recording?.transcription?.segments?.length ?? 0;
        await sendSystemMessage(threadId, `Transcript ready (${segs} segment${segs === 1 ? "" : "s"}): ${url}`);
        const a = await waitForMeetingArtifacts(recordingId);
        if (a.status !== "done" || !a.artifacts) return;
        const bits: string[] = [];
        const topics = a.artifacts.topics.length;
        const actions = a.artifacts.actionItems.length;
        if (topics) bits.push(`${topics} topic${topics === 1 ? "" : "s"}`);
        if (actions) bits.push(`${actions} action item${actions === 1 ? "" : "s"}`);
        const detail = bits.length ? ` (${bits.join(", ")})` : "";
        await sendSystemMessage(threadId, `Meeting insights ready${detail} [[insights:${recordingId}]]`);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[direct-call] post-record watcher crashed:", e);
      }
    })();
  }, []);

  const recordingToggling = useRef(false);
  const handleToggleRecording = useCallback(async () => {
    if (recordingToggling.current) return;
    if (callPhase !== "connected") return;
    recordingToggling.current = true;
    try {
      if (recording) {
        // Tell the remote first so their indicator drops promptly, even
        // before the local upload finishes.
        void sendRecordingEvent(SendDataEvent.RECORDING_STOPPED);
        const result = await stopRecording();
        const threadId = await resolveDmThreadId();
        await postRecordingResult(result, threadId);
      } else {
        const threadId = await resolveDmThreadId();
        const participants = [
          user ? { userId: String(user.id), screenName: user.screenName || "" } : null,
          opponent ? { userId: String(opponent.id), screenName: opponentName } : null,
        ].filter(Boolean) as Array<{ userId: string; screenName: string }>;
        const started = startRecording(recordingStreams, user ? String(user.id) : undefined, {
          threadId: threadId || undefined,
          participants,
        });
        if (started) {
          void sendRecordingEvent(SendDataEvent.RECORDING_STARTED);
          if (threadId) void sendSystemMessage(threadId, "Recording started");
        }
      }
    } finally {
      recordingToggling.current = false;
    }
  }, [
    callPhase,
    recording,
    sendRecordingEvent,
    stopRecording,
    resolveDmThreadId,
    postRecordingResult,
    user,
    opponent,
    opponentName,
    startRecording,
    recordingStreams,
  ]);

  // Auto-stop at the hard duration cap (mirrors ConferenceView) so the
  // recording is uploaded gracefully instead of exceeding the server's 2 GB
  // ceiling and being lost. Only the local recorder has `recording` true.
  // Reuses the toggle's stop path; the absolute deadline is
  // recordingStartedAt + cap, so re-arming on dep changes never drifts.
  useEffect(() => {
    if (!recording || !recordingStartedAt) return;
    const remaining = MAX_RECORDING_DURATION_MS - (Date.now() - recordingStartedAt);
    const timer = setTimeout(() => {
      void (async () => {
        const threadId = await resolveDmThreadId();
        if (threadId) {
          void sendSystemMessage(
            threadId,
            `Recording auto-stopped at ${Math.round(MAX_RECORDING_DURATION_MS / 60000)} min limit`
          );
        }
        await handleToggleRecording();
      })();
    }, Math.max(0, remaining));
    return () => clearTimeout(timer);
  }, [recording, recordingStartedAt, resolveDmThreadId, handleToggleRecording]);

  // Call duration timer
  const [duration, setDuration] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (callPhase === "connected") {
      timerRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [callPhase]);

  // Ringback tone for caller while ringing
  useEffect(() => {
    if (effectiveIsCaller && callPhase === "ringing") {
      startRingback();
    } else {
      stopRingback();
    }
    return () => stopRingback();
  }, [effectiveIsCaller, callPhase]);

  // Track whether we've navigated away to avoid rendering the overlay
  const [navigatedAway, setNavigatedAway] = useState(false);

  // Auto-redirect after call ends
  useEffect(() => {
    if (callPhase === "ended") {
      // If we were still recording when the call ended (other side hung up,
      // connection lost, etc.), stop and upload before navigating so the
      // file isn't lost. The upload + chat-side messages keep running after
      // navigation — they're fire-and-forget GraphQL mutations, no UI
      // attachment needed.
      if (recordingRef.current) {
        void (async () => {
          try {
            const result = await stopRecording();
            const threadId = await resolveDmThreadId();
            await postRecordingResult(result, threadId);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("[direct-call] auto-stop on call ended failed:", e);
          }
        })();
      }
      const timeout = setTimeout(() => {
        setNavigatedAway(true);
        history.push(Routes.messages);
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [callPhase, history, stopRecording, resolveDmThreadId, postRecordingResult]);

  // Attach remote stream to video element
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  // Blurred cover copy of the remote stream that fills the letterbox bars
  // around the `object-fit: contain` main element.
  const remoteBgVideoRef = useRef<HTMLVideoElement>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);

  const hasRemoteVideo = remoteStream.getVideoTracks().length > 0;
  const showRemoteVideo = callPhase === "connected" && hasRemoteVideo;

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream.getTracks().length > 0) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
    // The bg element is mounted conditionally (showRemoteVideo) so voice
    // calls don't hold a second decoder — re-attach when it appears.
    if (remoteBgVideoRef.current && remoteStream.getTracks().length > 0) {
      remoteBgVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream, showRemoteVideo]);

  useEffect(() => {
    if (localVideoRef.current && localStream.getTracks().length > 0) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  const formatDuration = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const handleEndCall = useCallback(async () => {
    // eslint-disable-next-line no-console
    console.warn(
      `[CALL-END-MUTATE] from=handleEndCall callId=${callId} recording=${recordingRef.current} (user clicked End button)`
    );
    stopRingback();
    if (recordingRef.current) {
      void sendRecordingEvent(SendDataEvent.RECORDING_STOPPED);
      // Stop + upload AND post the result to chat HERE. We must NOT defer the
      // posting to the callPhase==="ended" effect: stopRecording() flips
      // `recording` to false, so by the time endCall() drives callPhase to
      // "ended" that effect sees recordingRef.current === false and skips —
      // the file gets finalized on the server but is never linked in the chat
      // thread. (Remote-hangup / dropped-connection still reach the effect with
      // recording === true, so those paths post correctly.)
      const result = await stopRecording().catch(() => null);
      const threadId = await resolveDmThreadId();
      await postRecordingResult(result, threadId);
    }
    await endCall();
  }, [callId, endCall, sendRecordingEvent, stopRecording, resolveDmThreadId, postRecordingResult]);

  const hasLocalVideo = localStream.getVideoTracks().length > 0;

  // Either side recording → show indicator. We don't bother differentiating
  // who started it; what matters to the user is "this call is being
  // recorded, by someone."
  const recIndicatorStartedAt = recordingStartedAt ?? remoteRecordingStartedAt;

  const getStatusText = () => {
    switch (callPhase) {
      case "ringing":
        return "Calling...";
      case "connecting":
        return "Connecting...";
      case "connected":
        return formatDuration(duration);
      case "ended":
        if (endReason === "declined") return "Call declined";
        if (endReason === "cancelled") return "Call cancelled";
        return "Call ended";
    }
  };

  if (!user) {
    history.push(Routes.messages);
    return null;
  }

  // Don't render the fixed overlay after navigation to avoid blocking UI
  if (navigatedAway) {
    return null;
  }

  return (
    <div className={`page-direct-call page-direct-call--${callPhase}`}>
      {/* Reconnect banner — only while call is active (not ringing/ended) */}
      {callPhase === "connected" && connectionStatus && connectionStatus !== "connected" && (
        <div className="page-direct-call__connection-banner" data-status={connectionStatus}>
          {connectionStatus === "reconnecting" && t("Reconnecting...")}
          {connectionStatus === "disconnected" && t("Connection lost. Reconnecting soon...")}
          {connectionStatus === "failed" && t("Connection failed. Please rejoin the call.")}
        </div>
      )}

      {/* Blurred cover copy filling the letterbox bars behind the contained
          remote video (muted — the main element owns audio). Mounted only
          while remote video shows so voice calls don't pay for a second
          decoder + full-screen blur. */}
      {showRemoteVideo && (
        <video
          ref={remoteBgVideoRef}
          className="page-direct-call__remote-video-bg"
          autoPlay
          playsInline
          muted
          aria-hidden
        />
      )}
      {/* Remote video/audio element (always mounted so ref stays attached) */}
      <video
        ref={remoteVideoRef}
        className={
          callPhase === "connected" && hasRemoteVideo
            ? "page-direct-call__remote-video"
            : "page-direct-call__remote-audio"
        }
        autoPlay
        playsInline
        style={callPhase !== "connected" || !hasRemoteVideo ? { display: "none" } : undefined}
      />

      {/* Overlay content */}
      <div className="page-direct-call__overlay">
        {/* Top bar */}
        <div className="page-direct-call__top-bar">
          <div className="page-direct-call__top-bar-left">
            {recIndicatorStartedAt != null && (
              <span
                className="page-direct-call__rec-indicator"
                title={recording ? "You are recording this call" : "The other side is recording this call"}
              >
                <span className="page-direct-call__rec-dot" />
                <span>REC</span>
                <span className="page-direct-call__rec-time">
                  {formatDuration(Math.max(0, Math.floor((Date.now() - recIndicatorStartedAt) / 1000)))}
                </span>
              </span>
            )}
          </div>
          <div className="page-direct-call__top-bar-right">
            {callPhase === "connected" && producerWebRTC && <NetworkQualityBadge quality={quality} />}
            {callPhase === "connected" && (
              <span className="page-direct-call__encryption">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                </svg>
                End-to-end encrypted
              </span>
            )}
            {/* Copy-diagnostic button: always visible during a call so the
                user can capture the ring buffer the moment something feels
                off — including BEFORE any drop. Mirror of the group-call
                button in ConferenceView. */}
            {callPhase !== "ringing" && callPhase !== "ended" && (
              <button
                type="button"
                aria-label="Copy diagnostic logs"
                title="Скопировать диагностические логи в буфер обмена (для отправки разработчику)"
                onClick={async () => {
                  try {
                    // Wrap each getStats in a 3s timeout — after a transport
                    // teardown the mediasoup-client transport.getStats()
                    // can hang or reject; without the race the Copy button
                    // could lock the UI on an unresolved promise.
                    const withTimeout = <T,>(p: Promise<T>, label: string): Promise<T> =>
                      Promise.race([
                        p,
                        new Promise<T>((_, reject) =>
                          setTimeout(() => reject(new Error(`${label} timeout 3000ms`)), 3000)
                        ),
                      ]);
                    const producerClient = producerWebRTC?.webRTCData?.producer?.client;
                    const consumerClient = consumerWebRTC?.webRTCData?.consumer?.client;
                    const rtcEntries: Array<{ name: string; getStats?: () => Promise<unknown> }> = [];
                    if (producerClient?.getStats) {
                      rtcEntries.push({
                        name: "producer",
                        getStats: () => withTimeout(producerClient.getStats(), "producer getStats"),
                      });
                    }
                    if (consumerClient?.getStats) {
                      rtcEntries.push({
                        name: "consumer",
                        getStats: () => withTimeout(consumerClient.getStats(), "consumer getStats"),
                      });
                    }
                    await copyDumpToClipboard({
                      rtc: rtcEntries,
                      extra: { callId, callPhase, connectionStatus, quality, recording: recordingRef.current },
                    });
                    // eslint-disable-next-line no-alert
                    window.alert("Диагностика скопирована в буфер обмена. Отправьте её разработчику.");
                  } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error("[diag] copy failed", e);
                  }
                }}
                style={{
                  marginLeft: 8,
                  padding: "4px 10px",
                  borderRadius: 4,
                  border: "1px solid currentColor",
                  background: "rgba(0,0,0,0.35)",
                  color: "inherit",
                  cursor: "pointer",
                  fontSize: 12,
                }}
              >
                Copy diagnostic
              </button>
            )}
          </div>
        </div>

        {/* Center: avatar + status (shown when no remote video or ringing/connecting) */}
        {(callPhase !== "connected" || !hasRemoteVideo) && (
          <div className="page-direct-call__center">
            <div className="page-direct-call__avatar-wrapper">
              {callPhase === "ringing" && <div className="page-direct-call__pulse-ring" />}
              {opponent && <BaseAvatar className="page-direct-call__avatar" size={96} user={opponent} />}
            </div>
            <h2 className="page-direct-call__name">{opponentName}</h2>
            <p className="page-direct-call__status">{getStatusText()}</p>
            {callPhase === "ringing" && (
              <div className="page-direct-call__dots">
                <span />
                <span />
                <span />
              </div>
            )}
          </div>
        )}

        {/* Connected status bar (when remote video is showing) */}
        {callPhase === "connected" && hasRemoteVideo && (
          <div className="page-direct-call__connected-bar">
            <span className="page-direct-call__connected-name">{opponentName}</span>
            <span className="page-direct-call__connected-time">{formatDuration(duration)}</span>
          </div>
        )}

        {/* Local video PiP (self-view) */}
        <div
          className="page-direct-call__local-video-wrapper"
          style={callPhase !== "connected" || !hasLocalVideo ? { display: "none" } : undefined}
        >
          <video ref={localVideoRef} className="page-direct-call__local-video" autoPlay playsInline muted />
        </div>

        {/* Controls */}
        <div className="page-direct-call__controls">
          {callPhase === "ended" && (
            <button
              className="page-direct-call__btn"
              onClick={() => {
                setNavigatedAway(true);
                history.push(Routes.messages);
              }}
              type="button"
              title={t("Back to messages")}
              style={{
                background: "rgba(255,255,255,0.2)",
                width: "auto",
                borderRadius: 28,
                padding: "0 24px",
                fontSize: 14,
              }}
            >
              {t("Back to messages")}
            </button>
          )}
          {callPhase !== "ended" && (
            <>
              {/* Video toggle */}
              <button
                className={`page-direct-call__btn ${
                  !mediaConstraints.videoEnabled ? "page-direct-call__btn--off" : ""
                }`}
                onClick={toggleVideo}
                type="button"
                title={mediaConstraints.videoEnabled ? t("Turn off camera") : t("Turn on camera")}
                disabled={callPhase === "ringing"}
              >
                {mediaConstraints.videoEnabled ? (
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2L2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z" />
                  </svg>
                )}
              </button>

              {/* Audio toggle */}
              <button
                className={`page-direct-call__btn ${
                  !mediaConstraints.audioEnabled ? "page-direct-call__btn--off" : ""
                }`}
                onClick={toggleAudio}
                type="button"
                title={mediaConstraints.audioEnabled ? t("Mute") : t("Unmute")}
                disabled={callPhase === "ringing"}
              >
                {mediaConstraints.audioEnabled ? (
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" />
                  </svg>
                )}
              </button>

              {/* Record toggle — only visible once the call is established.
                  Hidden entirely when the *other* side is recording: only
                  one party records at a time (the file ends up in their
                  upload), and a second concurrent recorder would just
                  duplicate the artifact while the REC indicator already
                  tells you a recording is in progress. */}
              {(recording || remoteRecordingStartedAt == null) && (
                <button
                  className={`page-direct-call__btn ${recording ? "page-direct-call__btn--recording" : ""}`}
                  onClick={handleToggleRecording}
                  type="button"
                  title={recording ? t("Stop recording") : t("Start recording")}
                  disabled={callPhase !== "connected" || uploading}
                >
                  <BaseIcon size={24}>{recording ? "record_on" : "record_off"}</BaseIcon>
                </button>
              )}

              {/* End call */}
              <button
                className="page-direct-call__btn page-direct-call__btn--end"
                onClick={handleEndCall}
                type="button"
                title={t("End call")}
              >
                <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                  <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.956.956 0 010-1.36C3.69 8.68 7.62 7 12 7s8.31 1.68 11.71 4.72c.18.18.29.44.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28a11.27 11.27 0 00-2.67-1.85.996.996 0 01-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" />
                </svg>
              </button>
            </>
          )}
        </div>

        {/* Upload status — only when local recording finished and is uploading */}
        {uploading && <div className="page-direct-call__upload-status">{t("Uploading recording...")}</div>}
        {uploadError && (
          <div className="page-direct-call__upload-status page-direct-call__upload-status--error">{uploadError}</div>
        )}
      </div>
    </div>
  );
}
